import Service from '@/services/weave-x-service';

const AGENT_BASE_URL = '/api/agents';

type Thread = {
  thread_id: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    title?: string;
    sessionTitle?: string;
    [key: string]: unknown;
  } | null;
  status: string;
  values?: ThreadState | null;
};

type VizOutput = {
  full_html?: string;
  layout_html?: string;
  segments?: Record<string, string>;
  trigger?: string;
};

type FinalOutput = {
  viz_html?: string;
  narrative?: unknown;
  pass_with_disclaimer?: boolean;
};

type ThreadState = {
  messages: Message[];
  latestVisualizationCode?: string;
  threadName?: string;
  viz_current?: VizOutput | null;
  final_output?: FinalOutput | null;
  [key: string]: unknown;
};

type Message = {
  type?: string;
  role?: string;
  content: string | Array<unknown>;
  id: string | null;
  name?: string | null;
  metadata?: Record<string, unknown>;
};

type ChatHistoryResponse = {
  thread_id: string;
  messages: Message[];
  report?: Record<string, unknown> | null;
  state?: Record<string, unknown> | null;
};

export type ThreadMessagesResponse = Message[] | ChatHistoryResponse;

export type PlannerInvestorOption = {
  firmId: string;
  investorId: string;
  displayName: string;
  status: string;
  plannerSelectable: boolean;
  disabledReason?: string | null;
  profileReadiness?: string | null;
  portfolioFreshness?: string | null;
  importedPending?: boolean;
};

export type AgentId = 'canvas_agent' | 'weave_orchestrator' | 'orchestrator_v4';

export type CreateThreadOptions = {
  agentId?: AgentId;
  investorId?: string;
};

export async function createThread(
  clientId?: string,
  userId?: string,
  options: CreateThreadOptions = {},
): Promise<Thread> {
  return await Service.post(`${AGENT_BASE_URL}/threads/create`, {
    clientId,
    userId,
    ...(options.agentId ? { agentId: options.agentId } : {}),
    ...(options.investorId ? { investorId: options.investorId } : {}),
  });
}

export async function getPlannerInvestorOptions(firmId: string): Promise<PlannerInvestorOption[]> {
  return await Service.get(
    `${AGENT_BASE_URL}/firms/${encodeURIComponent(firmId)}/investors/planner-options`,
  );
}

export async function getAllThreads(userId: string): Promise<Thread[]> {
  return await Service.get(`${AGENT_BASE_URL}/threads/all`, {
    params: {
      user_id: userId,
    },
  });
}
export async function deleteThread(threadId: string): Promise<void> {
  await Service.delete(`${AGENT_BASE_URL}/threads/${threadId}`);
}

export async function getThreadMessages(threadId: string): Promise<ThreadMessagesResponse> {
  return await Service.get(`${AGENT_BASE_URL}/threads/${threadId}/chat/history`);
}

export async function updateThreadState(threadId: string, payload: Record<string, unknown>) {
  return await Service.patch(`${AGENT_BASE_URL}/threads/update-state`, {
    values: payload,
    threadId,
  });
}

export async function getThreadStateHistory(threadId: string) {
  return await Service.get(`${AGENT_BASE_URL}/threads/${threadId}/state/history`);
}

export async function cancelThreadRun(requestBody: { threadId: string; runId: string }) {
  return await Service.post(`${AGENT_BASE_URL}/runs/cancel`, requestBody);
}

// export async function deleteThreadRun(requestBody: {
//   threadId: string;
//   runId: string;
// }) {
//   return await Service.delete(`${AGENT_BASE_URL}/runs/delete`, requestBody);
// }

type Checkpoint = {
  checkpoint_id: string;
  checkpoint_ns: string;
  thread_id: string;
};

/**
 * Get all resumable checkpoints for a specific thread.
 * @param threadId The ID of the thread to retrieve checkpoints for.
 * @returns A promise that resolves to an array of Checkpoint objects. The checkpoints are ordered from newest to oldest.
 */
export async function getResumableCheckpoints(threadId: string): Promise<Checkpoint[]> {
  return await Service.get(`${AGENT_BASE_URL}/threads/${threadId}/resumable-checkpoints`);
}

export async function fetchChatHistoryOnCheckpoint(
  threadId: string,
  checkpoint: Checkpoint,
): Promise<Message[]> {
  return await Service.post(`${AGENT_BASE_URL}/threads/${threadId}/chat/history-by-checkpoint`, {
    checkpoint,
  });
}

export async function fetchThreadStateOnCheckpoint(
  threadId: string,
  checkpoint: Checkpoint,
): Promise<Exclude<ThreadState, 'messages'>> {
  return await Service.post(`${AGENT_BASE_URL}/threads/${threadId}/state-at-checkpoint`, {
    checkpoint,
  });
}

export async function patchThreadMetadata(
  threadId: string,
  metadata: Record<string, unknown>,
): Promise<void> {
  return await Service.patch(`${AGENT_BASE_URL}/threads/${threadId}/update`, {
    metadata,
  });
}

type GetThreadStateResponse = {
  metadata: Record<string, unknown>;
  values?: ThreadState | null;
  [key: string]: unknown;
};

export async function getThreadState(threadId: string): Promise<GetThreadStateResponse> {
  return await Service.get(`${AGENT_BASE_URL}/threads/${threadId}/state`);
}
